import React from 'react';
import {
  Chat,
  Channel,
  MessageInput,
  MessageList,
  Thread,
  Window,
  useMessageContext,
  Attachment,
  useRetryHandler,
  useDeleteHandler,
  useOpenThreadHandler,
  useMessageInputContext,
  AttachmentPreviewList as DefaultAttachmentPreviewList,
  useComponentContext,
  useChannelActionContext
} from 'stream-chat-react';
import { Button, Card, Divider, Modal, Typography } from 'antd';
import { useState, useEffect, useRef } from 'react';
import { ClockCircleOutlined, CloseOutlined, FolderOpenTwoTone, PaperClipOutlined, SendOutlined, UndoOutlined } from '@ant-design/icons';
import { StyleSheet } from '../../shared/custom-react-native';
import 'stream-chat-react/dist/css/v2/index.css';
import { useSelector } from "../../store";
import UI from '../../shared/Components/UI/js';
import { StreamChat } from "stream-chat";
import useDraftAPI from './useDraftApi';
import CustomMessageStatus from './CustomMessageStatus';
import TextareaAutosize from 'react-textarea-autosize';
import "./css.css";
import { event } from 'react-ga';

const EndChatModal = ({ open, handleClose, handleConfirm }) => {
  return (
    <Modal
      visible={open}
      mask={false}
      maskClosable={true}
      centered={false}
      closeIcon={false}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: '70%',
        bottom: 0,
        background: 'none',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
      footer={[
        <div style={styles.modal_container} key="footer">
          <Button style={styles.back_button} onClick={handleClose}>
            Back
          </Button>
          <Button style={styles.end_chat_button} onClick={handleConfirm}>
            End
          </Button>
        </div>,
      ]}
      width="40%"
    >
      <div style={{ padding: '1rem', borderRadius: '0.5rem' }}>
        <Typography weight={700} size={2} style={styles.modal_title}>
          End Chat?
        </Typography>
        <div style={{ width: '5rem' }}><Divider style={{ borderColor: '#51C6F2' }} /></div>
        <Typography weight={100} size={1} style={styles.modal_subtext}>
          Are you sure you want to end chat?
        </Typography>
      </div>
    </Modal>
  );
};

const DurationTimer = ({ secondsElapsed }) => {

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const formattedMinutes = minutes < 10 ? `0${minutes}` : `${minutes}`;
    const remainingSeconds = seconds % 60;
    return `${formattedMinutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  return formatTime(secondsElapsed);
};

const HtmlParser = ({ htmlString }) => {
  return <div dangerouslySetInnerHTML={{ __html: htmlString }} />;
};

const CustomMessageUiMetadata = ({ isMyMessage }) => {
  const {
    message: {
      created_at: createdAt,
      message_text_updated_at: messageTextUpdatedAt,
    },
  } = useMessageContext();

  return (
    <div className="custom-message-ui__metadata">
      <div className="custom-message-ui__metadata-created-at" style={{ fontSize: '0.7rem', display: 'flex', justifyContent: isMyMessage ? 'flex-end' : 'flex-start', }}>
        <CustomMessageStatus />
        {createdAt?.toLocaleString()}
      </div>
      {messageTextUpdatedAt && (
        <div
          className="custom-message-ui__metadata-edited-status"
          title={messageTextUpdatedAt}
          style={{ fontSize: '0.7rem', textAlign: isMyMessage ? 'right' : 'left' }}
        >
          Edited
        </div>
      )}
    </div>
  );
};

const CustomMessageUi = () => {
  const { message, isMyMessage } = useMessageContext();
  const handleDelete = useDeleteHandler(message);
  const handleReply = useOpenThreadHandler(message);
  const retrySend = useRetryHandler();
  const actionsRef = useRef();

  const statusStarted = UI.Constants.chat.status_started;
  const { appointmentStatus } = useSelector(
    (state) => state.getStreamToken
  );

  const [showOptions, setShowOptions] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const handleCopyMessage = (message) => {
    navigator.clipboard.writeText(message.text || "");
    setIsOpen(false);
  };

  const messageUiClassNames = ["custom-message-ui"];
  const hasAttachments = message.attachments && message.attachments.length > 0;

  if (isMyMessage()) {
    messageUiClassNames.push("custom-message-ui--mine");
  } else {
    messageUiClassNames.push("custom-message-ui--other");
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (actionsRef.current && !actionsRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleScrollOrWheel = () => {
      setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      window.addEventListener("wheel", handleScrollOrWheel);
      window.addEventListener("scroll", handleScrollOrWheel);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("wheel", handleScrollOrWheel);
      window.removeEventListener("scroll", handleScrollOrWheel);
    };
  }, [isOpen]);

  if (message.status === "failed") {
    const onRetry = () => retrySend(message);
    return (
      <div>
        <div
          style={{ borderRadius: '1.2rem', display: 'flex', justifyContent: isMyMessage() ? 'flex-end' : 'flex-start', alignItems: 'center' }}
        >
          <button onClick={onRetry} style={{ paddingRight: '1rem' }}>
            <UndoOutlined size={10} />
          </button>
          <div
            style={{
              backgroundColor: '#FFCEC9',
              maxWidth: '65%',
              borderRadius: '1rem',
              padding: '0.7rem 0.7rem 0.7rem 0.7rem',
              wordBreak: 'break-word',
            }}
          >
            {hasAttachments && <Attachment attachments={message.attachments} />}
            <HtmlParser htmlString={message.html} />
          </div>
        </div>
      </div>
    );
  }

  if (message.deleted_at || appointmentStatus !== statusStarted) {
    return (
      <div
        style={{
          borderRadius: '1.2rem',
          display: 'flex',
          justifyContent: isMyMessage() ? 'flex-end' : 'flex-start',
          alignItems: 'center'
        }}
      >
        <div
          style={{
            backgroundColor: isMyMessage() ? '#E9FBFF' : '',
            maxWidth: '65%',
            borderRadius: '1rem',
            padding: isMyMessage() ? '0.7rem 0.7rem 0.7rem 0.7rem' : '1rem 0rem 0rem 0rem',
            wordBreak: 'break-word',
            color: message.deleted_at ? '#999999' : '',
            fontStyle: message.deleted_at ? 'italic' : '',
            marginTop: '0.5rem'
          }}
        >
          <HtmlParser htmlString={message.type === 'deleted' ? 'This message was deleted.' : message.html} />
        </div>
      </div>
    )
  }

  return (
    <div
      className={messageUiClassNames.join(" ")}
      style={{ marginTop: '0.8rem' }}
      data-message-id={message.id}
    >
      <div
        style={{
          borderRadius: '1.2rem',
          display: 'flex',
          justifyContent: isMyMessage() ? 'flex-end' : 'flex-start',
          alignItems: 'center'
        }}
        onMouseOver={() => setShowOptions(true)}
        onMouseLeave={() => {
          if (!isOpen) {
            setShowOptions(false);
          }
        }}
      >
        {isMyMessage() && showOptions && (
          <>
            <div ref={actionsRef} style={{ paddingRight: "0.5rem", position: "relative", display: "inline-block" }}>
              <button onClick={() => {
                setIsOpen(!isOpen)
              }}>...</button>

              {isOpen && (
                <Card
                  style={{
                    position: "absolute",
                    bottom: "0",
                    right: "100%",
                    zIndex: 9999,
                    background: "white",
                    boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
                    borderRadius: "8px",
                    width: "10rem",
                    marginRight: '0.7rem'
                  }}
                >
                  <div
                    onClick={(e) => {
                      handleReply();
                      setIsOpen(false);
                      setShowOptions(false);
                    }}
                    style={styles.action_item_menu}
                  >
                    Reply
                  </div>
                  <div
                    onClick={() => {
                      handleCopyMessage(message);
                      setIsOpen(false);
                      setShowOptions(false);
                    }}
                    style={styles.action_item_menu}
                  >
                    Copy message
                  </div>
                  <div onClick={handleDelete} style={styles.action_item_menu}>
                    Delete message
                  </div>
                </Card>
              )}
            </div>
          </>
        )}
        <div
          style={{
            backgroundColor: isMyMessage() ? '#E9FBFF' : '',
            maxWidth: '65%',
            borderRadius: '1rem',
            padding: isMyMessage() ? '0.7rem 0.7rem 0rem 0.7rem' : '',
            wordBreak: 'break-word',
            color: message.type === 'deleted' ? '#999999' : '',
            fontStyle: message.type === 'deleted' ? 'italic' : ''
          }}
        >
          {hasAttachments && (
            <div>
              <Attachment attachments={message.attachments} />
            </div>
          )}
          <HtmlParser htmlString={message.html} />
        </div>
      </div>
      <CustomMessageUiMetadata isMyMessage={isMyMessage()} />
    </div>
  );
};

const CustomAttachmentPreview = () => {
  const { attachments, removeAttachment } = useMessageInputContext();

  return (
    <div className="custom-attachment-preview">
      {attachments.map((attachment, index) => (
        <div key={index} className="attachment-item">
          {attachment.type === "image" ? (
            <img src={attachment.image_url} alt="attachment" className="preview-image" />
          ) : (
            <div className="file-preview">
              <p>{attachment.file?.name || "File Attachment"}</p>
            </div>
          )}
          <button onClick={() => removeAttachment(attachment)}>Remove</button>
        </div>
      ))}
    </div>
  );
};

const OverrideSubmitHandler = (message, sendMessage, setText, removeAttachments) => {
  if (message?.attachments && message.attachments.length > 0) {
    const allAttachments = message.attachments.map((attachment) => {
      const attachmentMessage = {
        ...message,
        text: "",
        attachments: [attachment],
      };
      sendMessage(attachmentMessage);
      return attachment.localMetadata.id
    });
    removeAttachments(allAttachments);
  }

  if (message.text !== "") {
    const textMessage = { ...message, attachments: [] };
    sendMessage(textMessage);
    setText("");
  }
};

const CustomMessageInput = () => {
  const [willUpload, setWillUpload] = useState(false);
  const fileRef = useRef(null);

  const { handleUpdateDraftMessage, handleRemoveDraftMessage } = useDraftAPI()
  const { text, setText, handleChange, removeAttachments, uploadNewFiles, attachments } = useMessageInputContext();
  const { AttachmentPreviewList = DefaultAttachmentPreviewList } = useComponentContext();
  const { sendMessage } = useChannelActionContext();
  const { appointmentStatus } = useSelector(
    (state) => state.getStreamToken
  );

  const statusStarted = UI.Constants.chat.status_started;

  const setTextareaHeight = (e) => {
    handleUpdateDraftMessage(e)
    handleChange(e);
  }

  const handleOpenUploadOptions = () => {
    setWillUpload(true);
  };

  const handleCloseUploadOptions = () => {
    setWillUpload(false);
  }

  const handleOpenFileRef = () => {
    fileRef.current.click();
  };

  const handleFileChange = (e) => {
    const files = e.currentTarget.files;
    if (files && files.length > 0) {
      uploadNewFiles(files);
      e.currentTarget.value = "";
    }
    setWillUpload(false);
  }

  const onEnterPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onMessageSubmit();
    }
  }

  const onMessageSubmit = () => {
    OverrideSubmitHandler({ text, attachments }, sendMessage, setText, removeAttachments);
    handleRemoveDraftMessage();
  }

  return (
    <div className='message-input' style={{ display: 'flex', alignItems: 'center', gap: '2', width: '100%' }}>
      {willUpload ? (
        <div style={{ width: '100%', display: 'flex', flexDirection: 'row', height: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '10%', height: '100%' }}>
            <button onClick={handleCloseUploadOptions}>
              <CloseOutlined style={{ fontSize: '1.5rem', color: '#999999' }} />
            </button>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', width: '90%' }}>
            <div style={{ display: 'flex', justifyContent: 'center', flexDirection: 'column' }}>
              <button
                id="openFile"
                onClick={handleOpenFileRef}
                style={{ backgroundColor: '#E1F8FE', borderRadius: '100%', margin: '1.2rem', padding: '1rem' }}
              >
                <FolderOpenTwoTone style={{ fontSize: '2rem', color: '#36A0E8' }} />
              </button>
              <p style={{ color: '#5B6E7F', textAlign: 'center', fontWeight: 'bold' }}>Files</p>
            </div>
            <input id='openFile' ref={fileRef} type="file" multiple style={{ display: 'none' }} className="visually-hidden" onChange={(e) => handleFileChange(e)} />
          </div>
        </div>
      ) : (
        <div style={{ width: '100%' }}>
          {attachments.length > 0 && <AttachmentPreviewList />}
          <div style={{ width: '100%', display: 'flex', flexDirection: 'row' }}>
            <button onClick={handleOpenUploadOptions} style={{ marginLeft: '0.5rem' }} disabled={appointmentStatus !== statusStarted}>
              <PaperClipOutlined style={{ fontSize: '1.3rem', color: '#999999' }} />
            </button>
            <TextareaAutosize
              onChange={(e) => setTextareaHeight(e)}
              placeholder={appointmentStatus === statusStarted && 'Type your message here'}
              value={text}
              style={{
                width: '100%',
                margin: '0.5rem',
                padding: '0.2rem',
                borderRadius: '0.5rem',
                backgroundColor: '#EAEAEA',
                overflow: 'hidden'
              }}
              onKeyDown={(e) => onEnterPress(e)}
              className='message-input__input'
              disabled={appointmentStatus !== statusStarted}
            />
            <button onClick={onMessageSubmit} disabled={appointmentStatus !== statusStarted}>
              <SendOutlined style={{ fontSize: '1.3rem', color: '#0E4D94' }} />
            </button>
          </div>
        </div>
      )}
    </div>
  )
};

const Chatbox = ({ appointment, endChat, signAndFinalize }) => {
  const [chatClient, setChatClient] = useState(null);
  const [channel, setChannel] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [secondsElapsed, setSecondsElapsed] = useState(null);
  const [idleTimeElapsed, setIdleTimeElapsed] = useState(0);
  const [isIdle, setIsIdle] = useState(false);
  const { getStreamApiKey, getStreamToken, getStreamUserId, getStreamPatientId, appointmentStatus } = useSelector(
    (state) => state.getStreamToken
  );

  const { getMainInputDraft, handleRemoveDraftMessage } = useDraftAPI();
  const statusStarted = UI.Constants.chat.status_started;

  const activityEvents = [
    "mousemove",
    "keydown",
    "click",
    "scroll",
    "touchstart",
  ];

  activityEvents.forEach((event) => {
    if (appointmentStatus === statusStarted) {
      window.addEventListener(event, () => {
        // Avoid calling set state every activity event
        if (isIdle) {
          setTimeout(() => {
            /* Removed Idle notification after 2 secs */
            setIdleTimeElapsed(0);
            setIsIdle(false);
          }, 2000)
        }
      });
    }
  });

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleConfirm = async () => {
    if (channel && getStreamUserId && appointment?.id) {
      await handleThankYouMessage();
      endChat(appointment.id);
    } else {
      setOpenModal(false);
    }
  };

  const handleThankYouMessage = async () => {
    await channel.sendMessage({
      text: UI.Constants.chat.thank_you_message
    })
    await channel.update(
      { frozen: true },
      { text: UI.Constants.chat.chat_ended, getStreamUserId }
    );
    handleRemoveDraftMessage();
  }

  const customActions = {
    "Copy text": (message) => {
      navigator.clipboard.writeText(message.text || "");
    },
  };

  const user = appointment?.patient;
  const patientName = user?.name[0].given + " " + (user?.name[0].middle ?? "") + " " + user?.name[0].family;

  useEffect(() => {
    const initChat = async () => {
      const client = StreamChat.getInstance(getStreamApiKey, {
        timeout: 6000,
      });
      await client.connectUser({ id: getStreamUserId }, getStreamToken);
      setChatClient(client);
      if (!client) return;
      const getStreamChannelId = appointment?.id;
      const channel = client.channel(UI.Constants.GET_STREAM_CHANNEL_ID, getStreamChannelId, {
        members: [getStreamPatientId, getStreamUserId],
      });

      setChannel(channel);
    };

    if (getStreamUserId && getStreamPatientId && appointment) {
      initChat();
    }

    return () => {
      setChatClient(null); // ADD THIS LINE!
      chatClient?.disconnectUser();
    }
  }, [getStreamUserId, getStreamPatientId, appointment]);

  useEffect(async () => {
    if (channel && signAndFinalize && appointmentStatus === statusStarted) {
      await handleThankYouMessage();
    }
  }, [signAndFinalize]);

  const convertToSeconds = (value) => {
    return Math.floor(new Date(value).getTime() / 1000);
  };

  useEffect(() => {
    if (appointmentStatus === statusStarted) {
      const timer = setInterval(() => {
        if (appointment?.start) {
          const startSeconds = convertToSeconds(appointment.start);
          const nowSeconds = Math.floor(Date.now() / 1000);
          setSecondsElapsed(nowSeconds - startSeconds);
        }
        setIdleTimeElapsed((prevIdleTime) => {
          if (prevIdleTime >= UI.Constants.CHAT_IDLE_TIMER_LIMIT && !isIdle) {
            setIsIdle(true);
          }

          return prevIdleTime + 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [appointment?.start, isIdle]);

  if (!chatClient) return <div style={{ backgroundColor: 'white' }}>Loading connection...</div>;
  return (
    <div style={{ width: '100%', height: '100vh', overflow: 'hidden' }}>
      {chatClient && (<>
        <Chat client={chatClient}>
          <Channel channel={channel} Message={CustomMessageUi}>
            <Window>
              <EndChatModal
                open={openModal}
                handleClose={handleCloseModal}
                handleConfirm={handleConfirm}
              />
              <div style={styles.header_container}>
                <div style={{ padding: '0.7rem' }}>
                  <Typography style={styles.user_name}>{`${UI.capitalizeName(patientName)}`}</Typography>
                  {
                    appointmentStatus === statusStarted && secondsElapsed &&
                    <div style={styles.header_name}>
                      <ClockCircleOutlined size={3} style={styles.icon} />
                      <Typography style={{ fontSize: '1rem' }}><DurationTimer secondsElapsed={secondsElapsed} /></Typography>
                    </div>
                  }
                </div>
                {
                  appointmentStatus === statusStarted &&
                  <div style={styles.end_div}>
                    <Button style={styles.end_button} onClick={handleOpenModal}>
                      End Chat
                    </Button>
                  </div>
                }
              </div>
              {isIdle && (
                <div style={{ display: 'flex', justifyContent: 'center', alignContent: 'center' }}>
                  <div style={{ border: `2px solid #F89B22`, backgroundColor: '#FEF3E2', padding: '0.5rem 0.9rem 0.5rem 0.9rem', fontSize: '1rem', borderRadius: '0.5rem', display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center' }}>
                    <ClockCircleOutlined style={{ color: '#ED924A', marginRight: '0.8rem' }} />
                    <Typography style={{ color: '#ED924A', fontWeight: '500', marginRight: '1rem', }}>You have been idle for</Typography>
                    <Typography style={{ color: '#ED924A', fontWeight: '800' }}><DurationTimer secondsElapsed={idleTimeElapsed} />s</Typography>
                  </div>
                </div>
              )}
              <MessageList onlySenderCanEdit={true} customMessageActions={customActions} />
              <MessageInput
                Input={CustomMessageInput}
                additionalTextareaProps={{ maxRows: '9' }}
                getDefaultValue={getMainInputDraft}
                AttachmentPreview={CustomAttachmentPreview}
                overrideSubmitHandler={OverrideSubmitHandler}
                focus
              />
            </Window>
            <Thread />
          </Channel>
        </Chat>
      </>)}
    </div>
  )
};

export default Chatbox;

const styles = StyleSheet.create({
  header_container: {
    height: '10%',
    color: 'black',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingLeft: '2rem',
  },
  user_name: {
    fontSize: '1rem',
    fontWeight: 'bold',
    color: '#0E4D94'
  },
  header_name: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '3'
  },
  icon: {
    paddingRight: '0.3rem'
  },
  end_button: {
    backgroundColor: '#E74C3C',
    color: '#FFFFFF',
    fontWeight: 'bold',
    border: '1px solid #E74C3C',
    borderRadius: '0.3rem',
  },
  end_div: {
    display: 'flex',
    justifyContent: 'end',
    alignItems: 'center',
    paddingRight: '0.5rem',
  },
  back_button: {
    height: '2.25rem',
    width: '6rem',
    border: `1px solid #51C6F2`,
    color: '#51C6F2',
    fontWeight: 'bold',
    borderRadius: '1rem'
  },
  end_chat_button: {
    height: '2.25rem',
    width: '8rem',
    backgroundColor: '#51C6F2',
    color: '#FFFFFF',
    fontWeight: 'bold',
    borderRadius: '1rem'
  },
  modal_container: {
    display: 'flex',
    flexDirection: 'row',
    padding: '1rem',
    gap: '0.5rem',
    justifyContent: 'center',
  },
  modal_title: {
    fontSize: '1.7rem',
    fontWeight: 'bold'
  },
  modal_subtext: {
    marginTop: '1rem',
    fontSize: '1rem',
    color: 'black'
  },
  action_item_menu: {
    paddingBottom: '0.4rem',
    paddingTop: '0.4rem',
    cursor: 'pointer',
  },
});
